import { getFileItemsRefresh } from 'owa-groups-services';
import { isSuccess } from 'owa-groups-services';
import BaseMailboxFileItem from 'owa-service/lib/contract/BaseMailboxFileItem';
import GetFilesResponseType from 'owa-service/lib/contract/GetFilesResponseType';
import updateRecentFilesStore from 'owa-group-files-hub-store/lib/actions/updateRecentFilesStore';
import updateRecentFilesLoadingStatus from 'owa-group-files-hub-store/lib/actions/updateRecentFilesLoadingStatus';
import { getRecentFilesList } from 'owa-group-files-hub-store/lib/selectors/getRecentFilesList';
import { getFileItemsPromiseOverride } from 'owa-groups-adaptors';
import {
    FileInformationConverterForGetFileItems,
    MaxFiles,
} from '../utils/FileInformationConverterForGetFileItems';
import GetFileItemsCacheStatus from 'owa-service/lib/contract/GetFileItemsCacheStatus';
import FileListLoadingStatus from 'owa-group-files-hub-store/lib/Schema/FileListLoadingStatus';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import prefetchFilePreviewInformationAction from '../actions/PrefetchFilePreviewInformationAction';
import {
    logResponseDataForOwaServerCalls,
    isServiceFetchError,
    logServerCallErrorMessage,
    logErrorForEmptyOrNullResponse,
} from 'owa-server-call-logging/lib/owaServerCallLogging';
import { BaseFileInformation } from 'owa-group-files-hub-store';
import { logFileListInformation } from '../logging/logFileListInformation';
import { getErrorFromObject } from '../logging/genericInformationLogging';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';

function parseGetFileItemsRefreshResponse(
    response: any,
    datapoint: PerformanceDatapoint,
    groupSmtpAddress: string
) {
    if (datapoint) {
        datapoint.addCheckmark('ParsingResponse');
    }

    if (response && response.Body) {
        const recentFilesList = getRecentFilesList(groupSmtpAddress);

        if (isSuccess(response.Body)) {
            const fileList: BaseMailboxFileItem[] = response.Body.FileItems;
            const responseStatus: GetFilesResponseType = response.Body.ResponseStatusType;
            const cacheStatus: GetFileItemsCacheStatus = response.Body.CacheStatus;

            if (datapoint) {
                datapoint.addCustomData({
                    ResponseStatus: responseStatus.toString(),
                });
            }

            // success - refresh the list
            if (
                responseStatus == GetFilesResponseType.Success &&
                cacheStatus != null &&
                cacheStatus.IsRetrievedFromCache == false
            ) {
                recentFilesList.fileItems = createFileInformationListAndStartPreviewPrefetch(
                    fileList,
                    groupSmtpAddress,
                    datapoint
                );

                recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Success;
            }

            // failure - if initial call is successful and refresh call failed, we will still make the end state to be successful
            if (responseStatus == GetFilesResponseType.Failure) {
                if (
                    recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Failed ||
                    recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading
                ) {
                    recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Failed;
                } else {
                    recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Success;
                }
            }

            // partial success => if the list is still loading or cache call failed. i.e we dont have any response from cache, we populate the list else we ignore it.
            if (responseStatus == GetFilesResponseType.PartialSuccess) {
                if (
                    recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Failed ||
                    recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading
                ) {
                    recentFilesList.fileItems = createFileInformationListAndStartPreviewPrefetch(
                        fileList,
                        groupSmtpAddress,
                        datapoint
                    );
                }

                recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Success;
            }
        } else {
            if (datapoint) {
                logServerCallErrorMessage(datapoint, 'Server response is complete failure');
                datapoint.endWithError(
                    DatapointStatus.ServerError,
                    new Error('Server response is complete failure')
                );
            }

            // failure - if the list is still loading (no cache data), its an error scenario. else we just don't refresh
            if (
                recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Failed ||
                recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading
            ) {
                recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Failed;
            } else {
                recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Success;
            }
        }

        // Add to (if undefined), or update, the recent files list map
        updateRecentFilesStore(groupSmtpAddress, recentFilesList);
    } else {
        if (datapoint) {
            logErrorForEmptyOrNullResponse(datapoint, response);
            datapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(
                    'Error response from server encountered while trying to read the response'
                )
            );
        }
    }
}

function createFileInformationListAndStartPreviewPrefetch(
    fileItems: BaseMailboxFileItem[],
    groupSmtpAddress: string,
    datapoint: PerformanceDatapoint
): BaseFileInformation[] {
    const fileList = new FileInformationConverterForGetFileItems(
        fileItems,
        groupSmtpAddress,
        new Date() // For refresh call, use current time as the file retrieved time.
    ).getFileInformation();

    logFileListInformation(fileList, datapoint);

    // prefetch file preview information
    prefetchFilePreviewInformationAction(fileList);

    return fileList;
}

export function refreshRecentFiles(groupSmtpAddress: string) {
    const datapoint = new PerformanceDatapoint('GroupFilesHubGetFileItemsRefreshActionDP');

    datapoint.addCustomProperty('groupExternalId', getGroupMailboxExternalId(groupSmtpAddress));

    if (getFileItemsPromiseOverride) {
        getFileItemsPromiseOverride(groupSmtpAddress)
            .then((response: any) => {
                parseGetFileItemsRefreshResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                const recentFilesList = getRecentFilesList(groupSmtpAddress);
                if (recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading) {
                    updateRecentFilesLoadingStatus(groupSmtpAddress, FileListLoadingStatus.Failed);
                }

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    } else {
        getFileItemsRefresh(groupSmtpAddress, MaxFiles)
            .then((response: any) => {
                parseGetFileItemsRefreshResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                if (isServiceFetchError(error)) {
                    logResponseDataForOwaServerCalls(datapoint, error.response);
                }

                const recentFilesList = getRecentFilesList(groupSmtpAddress);
                if (recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading) {
                    updateRecentFilesLoadingStatus(groupSmtpAddress, FileListLoadingStatus.Failed);
                }

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    }
}
